import { useCallback, useEffect, useLayoutEffect, useRef, useState, type JSX } from 'react';
import { useStore } from '@/state/store';
import type { CreaseAssignment } from '@/origami/types';
import type { Vec2 } from '@/geometry/vec2';
import { dist2 } from '@/geometry/vec2';
import { fitViewport, screenToWorld, zoomAt, type Viewport } from './viewport';
import { computeSnap, type SnapResult } from './snapping';
import { hitTest } from './hitTest';
import { renderPattern } from './render';

type Drag =
  | { kind: 'none' }
  | { kind: 'pan'; last: Vec2 }
  | { kind: 'draw'; start: Vec2 }
  | { kind: 'move-vertex'; index: number }
  | { kind: 'measure'; start: Vec2 };

const TOOL_ASSIGNMENT: Partial<Record<string, CreaseAssignment>> = {
  crease: 'U',
  mountain: 'M',
  valley: 'V',
  flat: 'F',
  cut: 'C',
};

/**
 * The 2D crease-pattern editor.
 *
 * Rendering is immediate-mode into a single <canvas>: the pattern is small and
 * redrawing it costs less than reconciling a few thousand SVG nodes, and it
 * gives exact control over snap guides and device-pixel crispness.
 *
 * All input goes through Pointer Events, so mouse, pen and touch take the same
 * path; two-finger gestures pan and pinch-zoom.
 */
export function CreasePatternEditor(): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [viewport, setViewport] = useState<Viewport>({ scale: 1, tx: 0, ty: 0, width: 1, height: 1 });
  const [snap, setSnap] = useState<SnapResult | null>(null);
  const [draft, setDraft] = useState<{ a: Vec2; b: Vec2; assignment: string } | null>(null);
  const [measure, setMeasure] = useState<{ a: Vec2; b: Vec2 } | null>(null);
  const dragRef = useRef<Drag>({ kind: 'none' });
  const pointers = useRef(new Map<number, Vec2>());
  const pinchRef = useRef<{ distance: number; centre: Vec2 } | null>(null);
  const fittedFor = useRef<string>('');
  /** Once the user pans or zooms, stop re-fitting the view under them. */
  const userAdjusted = useRef(false);

  const pattern = useStore((s) => s.pattern);
  const analysis = useStore((s) => s.analysis);
  const tool = useStore((s) => s.tool);
  const selection = useStore((s) => s.selection);
  const hover = useStore((s) => s.hover);
  const snapSettings = useStore((s) => s.snap);
  const view = useStore((s) => s.view);
  const inspectedVertex = useStore((s) => s.inspectedVertex);
  const inspectedEdge = useStore((s) => s.inspectedEdge);

  const addCrease = useStore((s) => s.addCrease);
  const setHover = useStore((s) => s.setHover);
  const toggleSelection = useStore((s) => s.toggleSelection);
  const setSelection = useStore((s) => s.setSelection);
  const inspectVertex = useStore((s) => s.inspectVertex);
  const inspectEdge = useStore((s) => s.inspectEdge);
  const moveVertex = useStore((s) => s.moveVertex);
  const deleteSelection = useStore((s) => s.deleteSelection);

  // --- sizing -------------------------------------------------------------
  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const observer = new ResizeObserver(() => {
      const rect = wrap.getBoundingClientRect();
      setViewport((vp) => {
        if (vp.width === rect.width && vp.height === rect.height) return vp;
        // Re-fit on resize until the user has framed the view themselves. This
        // is what keeps the pattern visible when the pane changes shape - a
        // phone rotating, or the split divider being dragged.
        if (vp.width <= 1 || !userAdjusted.current) {
          return fitViewport(
            useStore.getState().pattern.verticesCoords,
            rect.width,
            rect.height,
          );
        }
        return { ...vp, width: rect.width, height: rect.height };
      });
    });
    observer.observe(wrap);
    return () => observer.disconnect();
  }, []);

  // Fit the view when a genuinely different pattern is loaded.
  useEffect(() => {
    const signature = `${pattern.meta.title}|${pattern.verticesCoords.length}|${pattern.edgesVertices.length}`;
    if (fittedFor.current === signature) return;
    if (viewport.width <= 1) return;
    fittedFor.current = signature;
    userAdjusted.current = false;
    setViewport(fitViewport(pattern.verticesCoords, viewport.width, viewport.height));
  }, [pattern, viewport.width, viewport.height]);

  // --- drawing ------------------------------------------------------------
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || viewport.width <= 1) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.floor(viewport.width * dpr);
    canvas.height = Math.floor(viewport.height * dpr);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    renderPattern(ctx, {
      pattern,
      analysis,
      viewport,
      selection,
      hover,
      highlightVertex: inspectedVertex,
      highlightEdge: inspectedEdge,
      showGrid: view.showGrid,
      gridDivisions: snapSettings.gridDivisions,
      showAuxiliary: view.showAuxiliary,
      snap,
      draft,
      measure,
      debug: view.debug,
      theme: view.theme,
      devicePixelRatio: dpr,
    });
  }, [
    pattern, analysis, viewport, selection, hover, snap, draft, measure,
    view.showGrid, view.showAuxiliary, view.debug, view.theme,
    snapSettings.gridDivisions, inspectedVertex, inspectedEdge,
  ]);

  const localPoint = useCallback((e: React.PointerEvent | PointerEvent): Vec2 => {
    const rect = canvasRef.current?.getBoundingClientRect();
    return { x: e.clientX - (rect?.left ?? 0), y: e.clientY - (rect?.top ?? 0) };
  }, []);

  const snapAt = useCallback(
    (screen: Vec2, anchor: Vec2 | null): SnapResult =>
      computeSnap(screenToWorld(viewport, screen), pattern, snapSettings, viewport, anchor),
    [pattern, snapSettings, viewport],
  );

  // --- pointer handling ---------------------------------------------------
  const onPointerDown = (e: React.PointerEvent): void => {
    (e.target as Element).setPointerCapture?.(e.pointerId);
    const p = localPoint(e);
    pointers.current.set(e.pointerId, p);

    if (pointers.current.size === 2) {
      const [a, b] = Array.from(pointers.current.values());
      pinchRef.current = {
        distance: Math.hypot(a.x - b.x, a.y - b.y),
        centre: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
      };
      dragRef.current = { kind: 'none' };
      setDraft(null);
      return;
    }

    const panRequested = e.button === 1 || e.button === 2 || e.shiftKey || tool === 'select' && e.altKey;
    if (panRequested) {
      dragRef.current = { kind: 'pan', last: p };
      return;
    }

    const world = screenToWorld(viewport, p);
    const assignment = TOOL_ASSIGNMENT[tool];

    if (assignment) {
      const s = snapAt(p, null);
      setSnap(s);
      dragRef.current = { kind: 'draw', start: s.point };
      setDraft({ a: s.point, b: s.point, assignment });
      return;
    }

    if (tool === 'measure') {
      const s = snapAt(p, null);
      dragRef.current = { kind: 'measure', start: s.point };
      setMeasure({ a: s.point, b: s.point });
      return;
    }

    const hit = hitTest(pattern, world, viewport);

    if (tool === 'move') {
      if (hit?.kind === 'vertex') {
        dragRef.current = { kind: 'move-vertex', index: hit.index };
        setSelection([hit]);
        return;
      }
      dragRef.current = { kind: 'pan', last: p };
      return;
    }

    if (tool === 'erase') {
      if (hit && hit.kind !== 'face') {
        setSelection([hit]);
        deleteSelection();
      }
      return;
    }

    // select
    if (hit) {
      toggleSelection(hit, e.shiftKey || e.metaKey || e.ctrlKey);
      if (hit.kind === 'vertex') inspectVertex(hit.index);
      else if (hit.kind === 'edge') inspectEdge(hit.index);
    } else {
      setSelection([]);
      inspectVertex(null);
    }
    dragRef.current = { kind: 'pan', last: p };
  };

  const onPointerMove = (e: React.PointerEvent): void => {
    const p = localPoint(e);
    if (pointers.current.has(e.pointerId)) pointers.current.set(e.pointerId, p);

    if (pointers.current.size === 2 && pinchRef.current) {
      const [a, b] = Array.from(pointers.current.values());
      const distance = Math.hypot(a.x - b.x, a.y - b.y);
      const centre = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      const prev = pinchRef.current;
      setViewport((vp) => {
        const zoomed = zoomAt(vp, centre, distance / Math.max(prev.distance, 1e-3));
        return {
          ...zoomed,
          tx: zoomed.tx + (centre.x - prev.centre.x),
          ty: zoomed.ty + (centre.y - prev.centre.y),
        };
      });
      pinchRef.current = { distance, centre };
      userAdjusted.current = true;
      return;
    }

    const drag = dragRef.current;
    switch (drag.kind) {
      case 'pan': {
        const dx = p.x - drag.last.x;
        const dy = p.y - drag.last.y;
        if (dx !== 0 || dy !== 0) userAdjusted.current = true;
        dragRef.current = { kind: 'pan', last: p };
        setViewport((vp) => ({ ...vp, tx: vp.tx + dx, ty: vp.ty + dy }));
        return;
      }
      case 'draw': {
        const s = snapAt(p, drag.start);
        setSnap(s);
        setDraft((d) => (d ? { ...d, b: s.point } : d));
        return;
      }
      case 'measure': {
        const s = snapAt(p, drag.start);
        setSnap(s);
        setMeasure({ a: drag.start, b: s.point });
        return;
      }
      case 'move-vertex': {
        const s = snapAt(p, null);
        setSnap(s);
        moveVertex(drag.index, s.point, false);
        return;
      }
      default: {
        const world = screenToWorld(viewport, p);
        setHover(hitTest(pattern, world, viewport));
        if (TOOL_ASSIGNMENT[tool] || tool === 'move' || tool === 'measure') {
          setSnap(snapAt(p, null));
        } else {
          setSnap(null);
        }
      }
    }
  };

  const onPointerUp = (e: React.PointerEvent): void => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinchRef.current = null;
    const drag = dragRef.current;
    dragRef.current = { kind: 'none' };

    if (drag.kind === 'draw' && draft) {
      const length = dist2(draft.a, draft.b);
      if (length > 1e-6) {
        addCrease(draft.a, draft.b, draft.assignment as CreaseAssignment);
      }
      setDraft(null);
      return;
    }
    if (drag.kind === 'move-vertex') {
      const p = localPoint(e);
      const s = snapAt(p, null);
      moveVertex(drag.index, s.point, true);
    }
  };

  const onWheel = (e: React.WheelEvent): void => {
    userAdjusted.current = true;
    const p = localPoint(e as unknown as React.PointerEvent);
    const factor = Math.exp(-e.deltaY * 0.0015);
    setViewport((vp) => zoomAt(vp, p, factor));
  };

  const fit = useCallback(() => {
    userAdjusted.current = false;
    setViewport((vp) => fitViewport(pattern.verticesCoords, vp.width, vp.height));
  }, [pattern]);

  useEffect(() => {
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'f' && !e.metaKey && !e.ctrlKey) {
        const target = e.target as HTMLElement;
        if (target.tagName === 'INPUT' || target.tagName === 'SELECT') return;
        fit();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [fit]);

  return (
    <div
      ref={wrapRef}
      className="pane pane-2d"
      style={{ flex: 1, touchAction: 'none' }}
      data-testid="editor-2d"
    >
      <div className="pane-label">2D crease pattern</div>
      <canvas
        ref={canvasRef}
        style={{ width: '100%', height: '100%', display: 'block', cursor: cursorFor(tool) }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onPointerLeave={() => {
          setSnap(null);
          setHover(null);
        }}
        onWheel={onWheel}
        onContextMenu={(e) => e.preventDefault()}
      />
      <button
        className="btn tiny"
        style={{ position: 'absolute', right: 10, top: 8 }}
        onClick={fit}
        title="Fit the pattern to the view (F)"
      >
        Fit
      </button>
    </div>
  );
}

function cursorFor(tool: string): string {
  if (TOOL_ASSIGNMENT[tool]) return 'crosshair';
  if (tool === 'move') return 'move';
  if (tool === 'measure') return 'crosshair';
  if (tool === 'erase') return 'not-allowed';
  return 'default';
}
